<template>
    <div class="content-box" style="min-height: 100%;">

        <h3>Foton på mottagningen</h3>
        <p>Välj upp till tre foton på din mottagning, inomhus och på ingången.</p>

        <div class="presentation-upload-box" v-if="locations.length < 3">
            <h2 v-if="!loading">Släpp filen här</h2>
            <h2 v-if="loading">Laddar...</h2>
            <input type="file" id="file_input" @change="upload" style="z-index:1;" v-if="!loading" />
        </div>

        <h4>Foton:</h4>

        <transition-group
            name="location-sort"
            tag="div"
            class="location-preview-row"
            :class="{ 'is-sorting': dragIndex !== null }"
            @dragover.native.prevent
            @drop.native="onDrop"
        >
            <div
                class="col"
                :key="locationKey(item.location, item.index)"
                v-for="(item, position) in displayedLocations"
                @dragenter="onDragEnter(position)"
                @dragover.prevent
            >
                <div
                    class="card h-100 position-relative"
                    :class="{ 'is-placeholder': dragIndex === item.index }"
                    style="border-radius: 16px; overflow: hidden; cursor: grab;"
                    draggable="true"
                    @dragstart="onDragStart(item.index, $event)"
                    @dragend="onDragEnd"
                >
                    <div class="location-popup"><img :src="imageUrl(item.location)" draggable="false"></div>
                    <image-crop-preview
                        v-if="item.location.locallyEdited"
                        class="card-img"
                        :src="rawUrl(item.location)"
                        :coordinates="cropCoordinates(item.location)"
                        :brightness="item.location.brightness || 0"
                    />
                    <img v-else class="card-img" :src="imageUrl(item.location)" draggable="false">
                    <span class="badge badge-warning" style="position:absolute; bottom:8px; left:8px;" v-if="item.location.locallyEdited">
                        Osparad ändring
                    </span>
                    <div class="card-img-overlay">
                        <button class="btn btn-sm btn-default" style="margin-right:6px;" @click="editingIndex = item.index">
                            Redigera
                        </button>
                        <button class="btn btn-sm btn-danger" @click="remove(item.index)">
                            <img src="/images/admin/icon-trash.svg" style="width: 20px;">
                        </button>
                    </div>
                </div>
            </div>
        </transition-group>

        <location-edit-modal
            v-if="editingIndex !== null"
            :location="locations[editingIndex]"
            @close="editingIndex = null"
        />

    </div>
</template>

<script>

import axios from 'axios'
import LocationEditModal from './location-edit-modal.vue'
import ImageCropPreview from './image-crop-preview.vue'

export default {

    components: {
        LocationEditModal,
        ImageCropPreview
    },

    props: {
        clinic_id: {
            required: true
        },
        locations: {
            required: true
        }
    },

    data () {
        return {
            loading: false,
            editingIndex: null,
            // Original index of the photo being dragged.
            dragIndex: null,
            // Live preview order while dragging (original indexes), null otherwise.
            dragOrder: null,
            // Ignore dragenter briefly after a move so the sliding photos
            // don't immediately trigger a move back.
            moveLockedUntil: 0
        }
    },

    computed: {
        displayedLocations () {
            const order = this.dragOrder || this.locations.map((location, index) => index)

            return order.map(index => ({ location: this.locations[index], index }))
        }
    },

    methods: {

        locationKey (location, index) {
            return 'l_' + (location.id || location.name || index)
        },

        onDragStart (index, event) {
            this.setDragGhost(event)

            event.dataTransfer.effectAllowed = 'move'
            // Firefox needs data set for the drag to start.
            event.dataTransfer.setData('text/plain', String(index))

            // Let the browser capture the drag image before the card turns
            // into a placeholder.
            setTimeout(() => {
                this.dragIndex = index
                this.dragOrder = this.locations.map((location, i) => i)
            }, 0)
        },

        onDragEnter (position) {
            if (this.dragOrder === null || Date.now() < this.moveLockedUntil) {
                return
            }

            const currentPosition = this.dragOrder.indexOf(this.dragIndex)

            if (currentPosition === position) {
                return
            }

            const order = this.dragOrder.slice()
            order.splice(currentPosition, 1)
            order.splice(position, 0, this.dragIndex)
            this.dragOrder = order
            this.moveLockedUntil = Date.now() + 250
        },

        onDrop () {
            if (this.dragOrder === null) {
                return
            }

            const order = this.dragOrder
            this.resetDrag()

            const changed = order.some((originalIndex, position) => originalIndex !== position)

            if (changed) {
                this.$emit('reorder', order.map(index => this.locations[index]))
            }
        },

        onDragEnd () {
            // Dropped outside the row or cancelled with Esc: revert the preview.
            this.resetDrag()
        },

        // Without this the browser snapshots the whole card including the
        // absolutely positioned hover popup above it, so the drag image gets a
        // large empty area and a wrong offset (the photo "drops in" from above).
        // Use a clean copy of just the photo, at the card's size, instead.
        setDragGhost (event) {
            const card = event.currentTarget
            const source = card.querySelector('.card-img')

            if (!source || !event.dataTransfer.setDragImage) {
                return
            }

            const rect = card.getBoundingClientRect()
            const ghost = document.createElement('div')

            Object.assign(ghost.style, {
                position: 'fixed',
                top: '-10000px',
                left: '-10000px',
                width: rect.width + 'px',
                height: rect.height + 'px',
                borderRadius: '16px',
                overflow: 'hidden',
                pointerEvents: 'none'
            })

            let copy

            if (source.tagName === 'CANVAS') {
                // cloneNode doesn't copy canvas pixels — redraw them.
                copy = document.createElement('canvas')
                copy.width = source.width
                copy.height = source.height
                copy.getContext('2d').drawImage(source, 0, 0)
            } else {
                copy = source.cloneNode(true)
                copy.style.objectFit = 'cover'
            }

            Object.assign(copy.style, { display: 'block', width: '100%', height: '100%' })
            ghost.appendChild(copy)
            document.body.appendChild(ghost)

            event.dataTransfer.setDragImage(ghost, event.clientX - rect.left, event.clientY - rect.top)

            // The browser has taken its snapshot once the event has finished.
            setTimeout(() => ghost.remove(), 0)
        },

        resetDrag () {
            this.dragIndex = null
            this.dragOrder = null
            this.moveLockedUntil = 0
        },

        rawUrl (location) {
            return '/storage/clinic-presentation/' + location.name + '.jpg'
        },

        cropCoordinates (location) {
            return {
                width: location.crop_width,
                height: location.crop_height,
                left: location.crop_left,
                top: location.crop_top
            }
        },

        imageUrl (location) {
            // edited_url already carries its own cache-busting query param.
            if (location.edited_url) {
                return location.edited_url
            }

            return this.rawUrl(location)
        },

        upload (event) {
            if (this.loading) {
                return
            }

            this.loading = true

            const files = event.target.files || event.dataTransfer.files
            if (!files.length) { return }

            const formData = new FormData()
            formData.append('file', files[0], files[0].name)
            formData.append('type', 'location')

            axios.post(
                '/clinics/' + this.clinic_id + '/presentation/upload',
                formData
            )
                .then(response => {
                    console.log('upload')
                    console.log(response.data)

                    this.$emit('upload', response.data)
                })
                .catch(error => {
                    alert('Sparing misslyckades, vänligen försök igen.')
                })
                .finally(() => {
                    this.loading = false
                })
        },

        remove (index) {
            if(confirm("Vill du verkligen ta bort bilden?"))
            {
                this.$emit('remove', index)
            }
        }
    }

}
</script>
